/**
 * Hash routing, with search params that several widgets can share.
 *
 * The hash scheme is kept exactly as it was (`#/companies/831641791`) so
 * links already in circulation keep working.
 *
 * The reason this is hand-written rather than a routing library: a page
 * now carries more than one independent list, each with its own filters,
 * page number and sort. Their state has to live in one query string
 * without colliding, so every widget gets a prefix — `#/municipalities/X
 * ?t.page=2&t.year=2021&c.q=строй` is the municipality's procurement list
 * on page 2 filtered to 2021, and its company list searching "строй".
 * `useScopedParams('t.')` hands a widget just its own slice.
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';

export interface Location {
  path: string;
  params: URLSearchParams;
}

function readLocation(): Location {
  const raw = window.location.hash.replace(/^#/, '') || '/';
  const cut = raw.indexOf('?');
  return cut === -1
    ? { path: raw, params: new URLSearchParams() }
    : { path: raw.slice(0, cut), params: new URLSearchParams(raw.slice(cut + 1)) };
}

const LocationContext = createContext<{
  location: Location;
  /** Writes params without re-running the router — see setParams below. */
  setParams: (next: URLSearchParams) => void;
}>({ location: { path: '/', params: new URLSearchParams() }, setParams: () => {} });

export function RouterProvider({ children }: { children: ReactNode }) {
  const [location, setLocation] = useState<Location>(readLocation);

  useEffect(() => {
    const onChange = () => setLocation(readLocation());
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);

  /*
   * Filter changes use replaceState, not a hash assignment. Assigning the
   * hash fires `hashchange`, which remounts the route — losing focus from
   * the search box mid-keystroke and resetting scroll. React already
   * re-renders from the state update, so the URL only needs to be brought
   * into line for sharing and reloading.
   */
  const setParams = useCallback((next: URLSearchParams) => {
    setLocation((current) => {
      const search = next.toString();
      window.history.replaceState(
        null, '', `#${current.path}${search ? `?${search}` : ''}`);
      return { path: current.path, params: next };
    });
  }, []);

  const value = useMemo(() => ({ location, setParams }), [location, setParams]);
  return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
}

export function useLocation() {
  return useContext(LocationContext);
}

export function navigate(path: string) {
  if (window.location.hash === `#${path}`) return;
  window.location.hash = path;
}

/** A widget's own slice of the query string, keyed by prefix. */
export interface ScopedParams {
  get: (key: string) => string | null;
  getAll: (key: string) => string[];
  /** Applies a batch of changes; null or '' removes a key. */
  set: (changes: Record<string, string | string[] | null | undefined>) => void;
  clear: () => void;
}

export function useScopedParams(prefix: string): ScopedParams {
  const { location, setParams } = useLocation();
  const { params } = location;

  return useMemo(() => {
    const full = (key: string) => `${prefix}${key}`;
    return {
      get: (key) => params.get(full(key)),
      // list values travel comma-separated: shorter and readable in a URL
      getAll: (key) => {
        const raw = params.get(full(key));
        return raw ? raw.split(',').filter(Boolean) : [];
      },
      set: (changes) => {
        const next = new URLSearchParams(params.toString());
        for (const [key, value] of Object.entries(changes)) {
          const joined = Array.isArray(value) ? value.join(',') : value;
          if (joined == null || joined === '') next.delete(full(key));
          else next.set(full(key), joined);
        }
        setParams(next);
      },
      clear: () => {
        const next = new URLSearchParams(params.toString());
        for (const key of [...next.keys()]) {
          if (key.startsWith(prefix)) next.delete(key);
        }
        setParams(next);
      },
    };
  }, [params, prefix, setParams]);
}

/** Whether the URL already carries a filter for this widget's prefix.

    A shared link is a claim about what the reader should see. If someone
    sends a company page with `?t.q=асфалт` on it, the section that filter
    belongs to has to be open when the page arrives, whatever the section
    would otherwise have decided for itself. */
export function useHasScopedParams(prefix: string): boolean {
  const { location } = useLocation();
  return useMemo(
    () => [...location.params.keys()].some((key) => key.startsWith(prefix)),
    [location.params, prefix],
  );
}

/* ------------------------------------------------------------------ routes */

export interface Route {
  pattern: string;
  render: (params: Record<string, string>) => ReactNode;
}

interface Compiled extends Route {
  regex: RegExp;
  names: string[];
}

function compile(route: Route): Compiled {
  const names: string[] = [];
  const source = route.pattern
    .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    .replace(/:(\w+)/g, (_match, name: string) => {
      names.push(name);
      return '([^/]+)';
    });
  return { ...route, regex: new RegExp(`^${source}$`), names };
}

export function Routes({ routes, fallback }: { routes: Route[]; fallback: ReactNode }) {
  const { location } = useLocation();
  const compiled = useMemo(() => routes.map(compile), [routes]);

  for (const route of compiled) {
    const match = location.path.match(route.regex);
    if (!match) continue;
    const params = Object.fromEntries(route.names.map((name, index) => [
      name, decodeURIComponent(match[index + 1] ?? ''),
    ]));
    return <>{route.render(params)}</>;
  }
  return <>{fallback}</>;
}
